// Utils
import { first } from 'lodash';

import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { getPhysicalAncestors } from '../../../../common/elements/utils/elementTraversalUtils';
import { isNavigableElement } from '../../../../common/elements/utils/elementTypeUtils';
import { hasReadPermission } from '../../../../common/permissions/elementPermissionsUtil';

// Types
import { ImMNElementMap } from '../../../../common/elements/elementModelTypes';
import { BreadcrumbResult, CompleteBreadcrumbResult } from '../breadcrumbTypes';

/**
 * Returns the physical path to the highest element readable by the user.
 */
export const getReadablePath = (currentBoardId: string, elements: ImMNElementMap, aclIds: string[]): string[] =>
    getPhysicalAncestors(elements, currentBoardId)
        .filter((ancestor) => hasReadPermission(elements, getElementId(ancestor), aclIds))
        .map((ancestor) => getElementId(ancestor))
        .reverse();

/**
 * Removes columns and skeletons from the visible breadcrumb list.
 */
const filterOutColumnsAndSkeletons =
    (elements: ImMNElementMap) =>
    (elementId: string): boolean => {
        const element = elements.get(elementId);
        return isNavigableElement(element);
    };

/**
 * Builds a complete breadcrumb result from a full path.
 */
export const createCompleteBreadcrumb = (
    fullPath: string[],
    elements: ImMNElementMap,
    detached: boolean,
    extra: Partial<BreadcrumbResult> = {},
): CompleteBreadcrumbResult => ({
    complete: true,
    detached,
    path: fullPath.filter(filterOutColumnsAndSkeletons(elements)),
    fullPath,
    ...extra,
});

/**
 * Builds the fallback breadcrumb for a password-protected shared board before the password has been entered.
 */
export const createProtectedBreadcrumb = (currentBoardId: string): BreadcrumbResult => ({
    complete: true,
    detached: true,
    protected: true,
    path: [currentBoardId],
    fullPath: [currentBoardId],
});

/**
 * Returns a complete breadcrumb for the readable physical path, if one exists.
 */
export const getReadableBreadcrumb = (
    currentBoardId: string,
    rootBoardId: string,
    elements: ImMNElementMap,
    aclIds: string[],
): CompleteBreadcrumbResult | undefined => {
    const readablePath = getReadablePath(currentBoardId, elements, aclIds);

    if (!readablePath.length) return;

    return createCompleteBreadcrumb(readablePath, elements, first(readablePath) !== rootBoardId);
};

/**
 * Identifies complete breadcrumbs that reach the user's root board.
 */
const isAttachedBreadcrumb = (breadcrumb?: BreadcrumbResult): boolean =>
    Boolean(breadcrumb?.complete && !breadcrumb.detached);

/**
 * Chooses the most useful breadcrumb, preferring attached paths over detached paths.
 */
export const chooseBreadcrumb = (
    preferredBreadcrumb: BreadcrumbResult | undefined,
    fallbackBreadcrumb: BreadcrumbResult | undefined,
): BreadcrumbResult | undefined => {
    if (isAttachedBreadcrumb(preferredBreadcrumb)) return preferredBreadcrumb;
    if (isAttachedBreadcrumb(fallbackBreadcrumb)) return fallbackBreadcrumb;

    if (preferredBreadcrumb?.complete) return preferredBreadcrumb;
    if (fallbackBreadcrumb?.complete) return fallbackBreadcrumb;

    return preferredBreadcrumb || fallbackBreadcrumb;
};
